import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import UserService from "../../service/UserService";
import Icon from "../element/icon/Icon";
import Badge from "../element/badge/Badge";
import {
  compareDriversByLastOnline,
  compareDriversByName,
  formatDriverLocationUpdatedAt,
  getDriverSearchText,
  getLatestDriverLocation,
  isDriverLocationActive
} from "../../state/driver/driverLocationUtils";

import classNames from "../../../node_modules/classnames/bind";
import styleNames from "./chatList.local.css";

const cx = classNames.bind(styleNames);

const LOCATION_STATUS_REFRESH_MS = 30 * 1000;
const SORT_BY_NAME = "name";
const SORT_BY_LAST_ONLINE = "lastOnline";

class ChatList extends Component {
  static propTypes = {
    drivers: PropTypes.array.isRequired,
    locations: PropTypes.shape({}),
    messages: PropTypes.array,
    handleDriverClicked: PropTypes.func,
    onDriverClick: PropTypes.func,
    /** Control Tower: center map only, never open chat */
    centerOnMapOnly: PropTypes.bool
  };

  static defaultProps = {
    centerOnMapOnly: false,
    locations: {}
  };

  constructor(props) {
    super(props);
    this.unreadMessages = {};
    this.toggle = [];
    this.state = {
      searchQuery: "",
      sortBy: SORT_BY_LAST_ONLINE
    };
  }

  componentDidMount() {
    this.locationStatusInterval = setInterval(() => {
      this.forceUpdate();
    }, LOCATION_STATUS_REFRESH_MS);
  }

  componentWillUnmount() {
    clearInterval(this.locationStatusInterval);
  }

  componentWillReceiveProps(nextProps) {
    const { messages } = nextProps;
    if (messages && messages.length && !messages[0].id) {
      if (!this.unreadMessages[messages[0].from_member_id]) {
        this.unreadMessages[messages[0].from_member_id] = 1;
      } else {
        this.unreadMessages[messages[0].from_member_id] += 1;
      }
    }
  }

  onDriverClicked = e => {
    e.preventDefault();
    e.stopPropagation();

    const { drivers, handleDriverClicked, onDriverClick, centerOnMapOnly } =
      this.props;
    const driverId = e.currentTarget.getAttribute("data-driver-id");

    if (!driverId) {
      return;
    }

    const driver = drivers.find(({ id }) => Number(id) === Number(driverId));

    if (!driver) {
      return;
    }

    if (centerOnMapOnly) {
      if (onDriverClick) {
        onDriverClick(driver);
      }
      return;
    }

    this.unreadMessages[driver.id] = 0;
    if (!this.toggle.includes(driver.id)) {
      this.toggle.push(driver.id);
      if (handleDriverClicked) {
        handleDriverClicked(driver, false);
      }
    } else {
      this.toggle.splice(this.toggle.indexOf(driver.id), 1);
      if (handleDriverClicked) {
        handleDriverClicked(driver, true);
      }
    }
  };

  handleSearchChange = e => {
    this.setState({ searchQuery: e.target.value });
  };

  handleSortChange = e => {
    this.setState({ sortBy: e.target.value });
  };

  getDisplayedDrivers() {
    const { drivers, locations, centerOnMapOnly } = this.props;
    const { searchQuery, sortBy } = this.state;

    let list = [...drivers];

    if (centerOnMapOnly) {
      const query = searchQuery.trim().toLowerCase();
      if (query) {
        list = list.filter(driver => getDriverSearchText(driver).includes(query));
      }

      if (sortBy === SORT_BY_LAST_ONLINE) {
        list.sort((a, b) => compareDriversByLastOnline(a, b, locations));
      } else {
        list.sort(compareDriversByName);
      }
    }

    return list;
  }

  renderDriverStatusIcon(driver) {
    const { locations, centerOnMapOnly } = this.props;

    if (centerOnMapOnly) {
      const isOnline = Number(driver.online) === 1;
      return (
        <Icon
          name="checkbox-blank-circle"
          className={cx(isOnline ? "chat-icon-online" : "chat-icon-inactive")}
        />
      );
    }

    if (Number(driver.online) === 1) {
      return (
        <Icon
          name="checkbox-blank-circle"
          className={cx("chat-icon-online")}
        />
      );
    }

    const location = getLatestDriverLocation(locations, driver.id);
    const updatedAt = formatDriverLocationUpdatedAt(location);

    if (!updatedAt) {
      return null;
    }

    const isActive = isDriverLocationActive(location);

    return (
      <Icon
        name="checkbox-blank-circle"
        className={cx(isActive ? "chat-icon-active" : "chat-icon-inactive")}
      />
    );
  }

  renderToolbar() {
    const { drivers, centerOnMapOnly } = this.props;
    const { searchQuery, sortBy } = this.state;
    const displayedCount = this.getDisplayedDrivers().length;

    if (!centerOnMapOnly) {
      return null;
    }

    return (
      <div className={cx("chat-list-toolbar")}>
        <div className={cx("chat-list-count")}>
          {displayedCount} von {drivers.length}
        </div>
        <input
          type="search"
          className={cx("chat-list-search")}
          placeholder="Fahrer suchen…"
          value={searchQuery}
          onChange={this.handleSearchChange}
          aria-label="Fahrer suchen"
        />
        <label className={cx("chat-list-sort-label")}>
          <span>Sortierung</span>
          <select
            className={cx("chat-list-sort")}
            value={sortBy}
            onChange={this.handleSortChange}
            aria-label="Sortierung"
          >
            <option value={SORT_BY_NAME}>Name</option>
            <option value={SORT_BY_LAST_ONLINE}>Zuletzt online</option>
          </select>
        </label>
      </div>
    );
  }

  render() {
    const { locations } = this.props;
    const displayedDrivers = this.getDisplayedDrivers();

    return (
      <div className={cx("chat-list-root")}>
        {this.renderToolbar()}
        <ul className={cx("chat-item-list")}>
        {displayedDrivers.map(driver => {
          const location = getLatestDriverLocation(locations, driver.id);
          const updatedAt = formatDriverLocationUpdatedAt(location);

          return (
            <li key={`driver-${driver.id}`} className={cx("chat-item")}>
              <button
                type="button"
                className={cx("chat-item-button")}
                onClick={this.onDriverClicked}
                data-driver-id={driver.id}
              >
                <div className={cx("chat-icon")}>
                  {this.renderDriverStatusIcon(driver)}
                </div>
                <div className={cx("message-badge")}>
                  {this.unreadMessages[driver.id] > 0 && (
                    <Badge text={this.unreadMessages[driver.id]} />
                  )}
                </div>
                <div className={cx("chat-user-block")}>
                  <div className={cx("chat-user")}>
                    {UserService.getName(driver)}
                  </div>
                  {updatedAt ? (
                    <div className={cx("chat-user-meta")} title="Letzte Standortaktualisierung">
                      {updatedAt}
                    </div>
                  ) : (
                    <div className={cx("chat-user-meta", "chat-user-meta-empty")}>
                      Kein Standort heute
                    </div>
                  )}
                </div>
              </button>
            </li>
          );
        })}
        </ul>
      </div>
    );
  }
}

const mapStateToProps = ({ auth: { token }, dialog: { messages }, driver }) => ({
  token,
  messages,
  locations: driver.locations
});

export default connect(mapStateToProps)(ChatList);
